import React, { useCallback, useEffect, useState } from 'react';
import { apiErrorMessage } from '../../utils/apiError';
import mcpServiceService from '../../services/mcpServiceService';
import Button from '../shared/Button';
import ConfirmModal from '../shared/ConfirmModal';
import McpBackendTab from './McpBackendTab';

// MCP AS A SERVICE — CONTENT (doc/MCP-AS-A-SERVICE.md §8, V639). One hotel's content, as agents will read it:
//
//   Review     what a website crawl (or a person) proposes — each with the published text it would replace, so a
//              change is seen before it is accepted. Nothing reaches an agent until it is published (FR-053).
//   Published  what agents read now. Editing sends it back to review; the published text stays live until then.
//   Crawls     Crawl now, and the history (pages found, fetched, changed, items proposed).
//   Insights   questions agents asked that nothing answers (feature 3), and llms.txt / JSON-LD for the hotel's own
//              website (feature 2).
//   HCONNECT MCP Backend   what the backend actually holds for the hotel, read back through its binding (McpBackendTab)
//              — publish, then see it arrive.
//
// Editing is for the hotel's admins (canEdit, decided by the server).

const TOPICS = ['description', 'roomTypes', 'facilities', 'dining', 'spa', 'meetings', 'localArea', 'policies',
  'faqs', 'offers', 'accessibility', 'sustainability', 'images'];
const VIEWS = [['review', 'Review'], ['published', 'Published'], ['crawls', 'Crawls'], ['insights', 'Insights'],
  ['backend', 'HCONNECT MCP Backend']];
const when = (v) => (v ? new Date(v).toLocaleString() : '—');
// PUBLISHED IS TWO STATES, and the screen says which: published here (a person approved it) and live for agents (the
// content publisher, every minute, has sent this version to the MCP Backend — synced_version caught up).
const isLive = (i) => i.synced_version != null && Number(i.synced_version) === Number(i.published_version);
const POLL_MS = 15000;
const EMPTY = { topic: 'description', language: 'en', title: '', body: '', questions: '', guardrail: false, sourceUrl: '' };

export default function McpContentTab({ hotels, initialView = 'review' }) {
  const [hotelId, setHotelId] = useState(hotels[0] ? hotels[0].hotelId : null);
  const hotel = hotels.find(h => String(h.hotelId) === String(hotelId));
  const edit = !!(hotel && hotel.canEdit);
  const [view, setView] = useState(initialView);
  const [items, setItems] = useState([]);
  const [crawls, setCrawls] = useState([]);
  const [insights, setInsights] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    if (!hotelId) return;
    setError(null);
    try {
      if (view === 'crawls') setCrawls((await mcpServiceService.listCrawls(hotelId)).crawls);
      else if (view === 'insights') setInsights(await mcpServiceService.getInsights(hotelId, 30));
      else setItems((await mcpServiceService.listContent(hotelId)).items);
    } catch (e) { setError(apiErrorMessage(e)); }
  }, [hotelId, view]);
  useEffect(() => { load(); }, [load]);

  const act = async (what, fn, done) => {
    setBusy(what); setError(null); setNotice(null);
    try { await fn(); await load(); if (done) setNotice(done); } catch (e) { setError(apiErrorMessage(e)); } finally { setBusy(null); }
  };

  // While anything published is still on its way, look again every 15s so it turns live without a reload.
  const waiting = items.filter(i => i.status === 'published' && !isLive(i)).length;
  useEffect(() => {
    if (!waiting || (view !== 'review' && view !== 'published')) return undefined;
    const t = setInterval(() => { load(); }, POLL_MS);
    return () => clearInterval(t);
  }, [waiting, view, load]);

  const shown = items.filter(i => (view === 'review' ? ['review', 'draft'].includes(i.status) : i.status === 'published'));

  return (
    <div className="mcps-section">
      {/* One hotel's page passes just that hotel — no picker then. */}
      {hotels.length > 1 && <div className="mcps-grid">
        <label>Hotel<select value={hotelId || ''} onChange={e => setHotelId(e.target.value)}>
          {hotels.map(h => <option key={h.hotelId} value={h.hotelId}>{h.hotelName}</option>)}</select></label>
      </div>}
      <div className="mcps-tabs">
        {VIEWS.map(([k, label]) => (
          <button key={k} type="button" className={`mcps-tab ${view === k ? 'active' : ''}`} onClick={() => setView(k)}>
            {label}{k === 'review' && items.filter(i => ['review', 'draft'].includes(i.status)).length
              ? ` (${items.filter(i => ['review', 'draft'].includes(i.status)).length})` : ''}
            {k === 'published' && items.some(i => i.status === 'published')
              ? ` (${items.filter(i => i.status === 'published').length})` : ''}</button>
        ))}
      </div>
      {error && <div className="os-msg os-msg-error">{error}</div>}
      {notice && <div className="os-msg os-msg-success">{notice}</div>}
      {view === 'published' && waiting > 0 && (
        <p className="mcps-meta">{waiting} published item{waiting === 1 ? ' is' : 's are'} on the way to the MCP Backend —
          the publisher sends them within a minute. This page updates by itself.</p>)}

      {(view === 'review' || view === 'published') && (
        <>
          {edit && <div className="mcps-actions"><Button variant="secondary" onClick={() => setEditing({ ...EMPTY })}>Add an item</Button></div>}
          {!shown.length && <p className="mcps-meta">{view === 'review'
            ? 'Nothing waiting for review. Crawl the website, or add an item.' : 'Nothing published yet.'}</p>}
          {shown.map(i => (
            <div key={i.id} className="mcps-card">
              <div className="mcps-card-head">
                <h3>{i.status === 'published' && (isLive(i)
                  ? <span className="mcps-pill mcps-pill-verified" title="The MCP Backend holds this version; agents read it">Live for agents</span>
                  : <span className="mcps-pill mcps-pill-pending" title="Published here; the publisher sends it to the MCP Backend within a minute">Waiting to send</span>)}{' '}
                  {i.title} <span className="mcps-meta">· {i.topic} · {i.language}{i.guardrail === 'Y' ? ' · quoted word for word' : ''}</span></h3>
                <span className="mcps-meta">{i.origin === 'crawl' ? `from the website${i.confidence != null ? ` (${Math.round(i.confidence * 100)}% sure)` : ''}` : 'added by a person'}</span>
              </div>
              {i.previous_body && i.previous_body !== i.body ? (
                <div className="mcps-diff">
                  <div><div className="mcps-meta">Published now</div><div className="mcps-diff-old">{i.previous_body}</div></div>
                  <div><div className="mcps-meta">Proposed</div><div className="mcps-diff-new">{i.body}</div></div>
                </div>
              ) : <div className="mcps-body">{i.body}</div>}
              {i.questions && i.questions.length > 0 && <div className="mcps-meta">Answers: {i.questions.join(' · ')}</div>}
              {i.source_url && <div className="mcps-meta">Source: <a href={i.source_url} target="_blank" rel="noreferrer">{i.source_url}</a></div>}
              {i.status === 'published' && <div className="mcps-meta">Published {when(i.published_at)} by {i.published_by}</div>}
              {edit && <div className="mcps-actions">
                {i.status !== 'published' && <Button variant="primary" disabled={!!busy}
                    onClick={() => act(`pub-${i.id}`, () => mcpServiceService.publishContent(hotelId, i.id),
                      `Published “${i.title}”. It is under Published, and goes live for agents within a minute.`)}>
                  {busy === `pub-${i.id}` ? 'Publishing…' : 'Publish'}</Button>}
                <Button variant="secondary" disabled={!!busy} onClick={() => setEditing({
                  id: i.id, topic: i.topic, language: i.language, title: i.title, body: i.body,
                  questions: (i.questions || []).join('\n'), guardrail: i.guardrail === 'Y', sourceUrl: i.source_url || '' })}>Edit</Button>
                <Button variant="danger" disabled={!!busy}
                        onClick={() => act(`arc-${i.id}`, () => mcpServiceService.archiveContent(hotelId, i.id))}>
                  {i.status === 'published' ? 'Withdraw' : 'Reject'}</Button>
              </div>}
            </div>
          ))}
        </>
      )}

      {view === 'crawls' && (
        <>
          <p className="mcps-meta">The crawler reads the public pages of the website set on the Hotels tab (respecting robots.txt),
            and proposes content for review. Set <em>weekly</em> there to re-crawl every Monday.</p>
          {edit && <Button variant="primary" disabled={!!busy}
                           onClick={() => act('crawl', () => mcpServiceService.startCrawl(hotelId))}>{busy === 'crawl' ? 'Starting…' : 'Crawl now'}</Button>}
          <table className="mcps-table">
            <thead><tr><th>Started</th><th>Site</th><th>Status</th><th>Pages</th><th>Changed</th><th>Proposed</th><th>By</th></tr></thead>
            <tbody>{crawls.map(c => (
              <tr key={c.id}><td>{when(c.started_at || c.created_at)}</td><td className="mcps-mono">{c.start_url}</td>
                <td>{c.status}{c.error ? <div className="mcps-meta">{c.error}</div> : null}</td>
                <td>{c.pages_fetched}/{c.pages_found}</td><td>{c.pages_changed}</td><td>{c.items_proposed}</td><td>{c.requested_by}</td></tr>))}</tbody>
          </table>
        </>
      )}

      {view === 'insights' && insights && (insights.available ? (
        <>
          <div className="mcps-card">
            <h3>Questions agents asked that your content does not answer (last {insights.days} days)</h3>
            {insights.gaps.length ? (
              <table className="mcps-table"><thead><tr><th>Question</th><th>Times</th><th>Last asked</th></tr></thead>
                <tbody>{insights.gaps.map(g => <tr key={g.question}><td>{g.question}</td><td>{g.times}</td><td>{when(g.lastAskedAt)}</td></tr>)}</tbody></table>
            ) : <p className="mcps-meta">None — every question had an answer.</p>}
          </div>
          <div className="mcps-card">
            <h3>llms.txt</h3>
            <p className="mcps-meta">Publish this at <span className="mcps-mono">/llms.txt</span> on the hotel&rsquo;s website so AI assistants can find it.</p>
            <textarea className="mcps-code" readOnly value={insights.llmsTxt} rows={12} />
          </div>
          <div className="mcps-card">
            <h3>Structured data (JSON-LD)</h3>
            <p className="mcps-meta">Add to the hotel&rsquo;s home page inside <span className="mcps-mono">&lt;script type="application/ld+json"&gt;</span>.</p>
            <textarea className="mcps-code" readOnly value={JSON.stringify(insights.jsonLd, null, 2)} rows={12} />
          </div>
        </>
      ) : <div className="os-msg">{insights.reason}</div>)}

      {view === 'backend' && hotel && <McpBackendTab key={hotel.hotelId} hotel={hotel} />}

      {editing && (
        <ConfirmModal title={editing.id ? 'Edit content' : 'Add content'} confirmLabel="Save for review" wide
                      onCancel={() => setEditing(null)}
                      onConfirm={() => {
                        const body = { ...editing, questions: editing.questions.split('\n').map(q => q.trim()).filter(Boolean) };
                        const id = editing.id;
                        setEditing(null);
                        act('save', () => (id ? mcpServiceService.saveContent(hotelId, id, body) : mcpServiceService.createContent(hotelId, body)));
                      }}>
          <div className="mcps-grid">
            <label>Topic<select value={editing.topic} onChange={e => setEditing(x => ({ ...x, topic: e.target.value }))}>
              {TOPICS.map(t => <option key={t} value={t}>{t}</option>)}</select></label>
            <label>Language<input value={editing.language} maxLength={5} onChange={e => setEditing(x => ({ ...x, language: e.target.value }))} /></label>
            <label>Title<input value={editing.title} maxLength={300} onChange={e => setEditing(x => ({ ...x, title: e.target.value }))} /></label>
            <label>Source URL<input value={editing.sourceUrl} onChange={e => setEditing(x => ({ ...x, sourceUrl: e.target.value }))} /></label>
          </div>
          <label className="mcps-wide">Text<textarea rows={8} value={editing.body} onChange={e => setEditing(x => ({ ...x, body: e.target.value }))} /></label>
          <label className="mcps-wide">Questions it answers (one per line)<textarea rows={4} value={editing.questions} onChange={e => setEditing(x => ({ ...x, questions: e.target.value }))} /></label>
          <label className="mcps-check"><input type="checkbox" checked={editing.guardrail} onChange={e => setEditing(x => ({ ...x, guardrail: e.target.checked }))} />
            Agents must quote this word for word (terms, times, fees)</label>
        </ConfirmModal>
      )}
    </div>
  );
}
